import React, { useState, useEffect, memo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Modal,
  LinearProgress,
  Fade,
  Collapse,
} from '@mui/material';
import {
  Check,
  Send,
  Shield,
  Email,
  Phone,
  Close,
  LockOutlined,
  CheckCircle,
  ArrowForward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import nodeAxiosFirebase from '../../utils/nodeAxiosFirebase';
import { setUser } from '../../store/coreSlice';
import { getSemanticColors } from '../../utils/semanticColors';
import { toast } from 'react-toastify';

// Verification Card Component - defined outside to prevent re-renders
const VerificationCard = memo(({
  icon: Icon,
  title,
  subtitle,
  isVerified,
  value,
  setValue,
  code,
  setCode,
  codeSent,
  onSendCode,
  onVerifyCode,
  sendLoading,
  verifyLoading,
  placeholder,
  codePlaceholder,
  isEmailType = false,
  isPhoneType = false,
  isDarkMode,
  mainColor,
  successColor,
  t,
}) => {
  // For phone, extract only digits and check if there are exactly 10
  const phoneDigits = isPhoneType ? value.replace(/\D/g, '') : '';
  const isPhoneValid = !isPhoneType || phoneDigits.length === 10;
  const canSend = value && isPhoneValid;

  return (
  <Box
    sx={{
      position: 'relative',
      p: 2.5,
      borderRadius: 3,
      border: `1px solid ${isVerified 
        ? (isDarkMode ? '#22c55e40' : '#22c55e30')
        : (isDarkMode ? '#ffffff15' : '#00000010')}`,
      bgcolor: isVerified
        ? (isDarkMode ? '#22c55e10' : '#22c55e08')
        : (isDarkMode ? '#ffffff08' : '#ffffff'),
      boxShadow: isVerified
        ? '0 0 20px rgba(34, 197, 94, 0.1)'
        : (isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)'),
      transition: 'all 0.3s ease',
      overflow: 'hidden',
    }}
  >
    {/* Success overlay */}
    {isVerified && (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.03) 0%, rgba(34, 197, 94, 0.08) 100%)',
          pointerEvents: 'none',
        }}
      />
    )}

    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      {/* Icon */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isVerified
            ? successColor
            : (isDarkMode ? '#ffffff10' : '#f3f4f6'),
          color: isVerified ? '#fff' : (isDarkMode ? '#fff' : '#6b7280'),
          transition: 'all 0.3s ease',
          flexShrink: 0,
        }}
      >
        {isVerified ? <CheckCircle /> : <Icon />}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#fff' : '#111827',
              fontSize: '0.95rem',
            }}
          >
            {title}
          </Typography>
          {isVerified && (
            <Fade in>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 10,
                  bgcolor: successColor,
                  color: '#fff',
                }}
              >
                <Check sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('verified')}
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            fontSize: '0.8rem',
            mb: isVerified ? 0 : 2,
          }}
        >
          {isVerified ? value : subtitle}
        </Typography>

        {/* Input Fields */}
        {!isVerified && (
          <Collapse in={!isVerified}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Value Input */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={placeholder}
                  disabled={codeSent}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isDarkMode ? '#ffffff08' : '#f9fafb',
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: isDarkMode ? '#ffffff15' : '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? '#ffffff25' : '#d1d5db',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: mainColor,
                        borderWidth: 1,
                      },
                    },
                    '& input': {
                      fontSize: '0.875rem',
                      color: isDarkMode ? '#fff' : '#111827',
                      '&::placeholder': {
                        color: isDarkMode ? '#6b7280' : '#9ca3af',
                      },
                    },
                  }}
                />
                {!codeSent && (
                  <IconButton
                    onClick={onSendCode}
                    disabled={!canSend || sendLoading}
                    sx={{
                      width: 44,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: canSend ? mainColor : (isDarkMode ? '#ffffff10' : '#e5e7eb'),
                      color: canSend ? '#fff' : (isDarkMode ? '#6b7280' : '#9ca3af'),
                      '&:hover': {
                        bgcolor: canSend ? mainColor : (isDarkMode ? '#ffffff15' : '#d1d5db'),
                      },
                      '&:disabled': {
                        bgcolor: isDarkMode ? '#ffffff10' : '#e5e7eb',
                        color: isDarkMode ? '#4b5563' : '#9ca3af',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {sendLoading ? (
                      <CircularProgress size={18} sx={{ color: 'inherit' }} />
                    ) : (
                      <Send sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                )}
              </Box>

              {/* Code Input or Email Check */}
              <Collapse in={codeSent}>
                {isEmailType ? (
                  // Email verification - show message and check status button
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: isDarkMode ? '#3b82f610' : '#eff6ff',
                        border: `1px solid ${isDarkMode ? '#3b82f640' : '#bfdbfe'}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: isDarkMode ? '#93c5fd' : '#1e40af',
                          lineHeight: 1.5,
                        }}
                      >
                        📧 {t('checkYourInbox') || 'Check your inbox and click the verification link we sent you.'}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={onVerifyCode}
                      disabled={verifyLoading}
                      sx={{
                        width: '100%',
                        height: 40,
                        borderRadius: 2,
                        bgcolor: successColor,
                        color: '#fff',
                        fontSize: '0.8rem',
                        '&:hover': {
                          bgcolor: '#16a34a',
                        },
                        '&:disabled': {
                          bgcolor: isDarkMode ? '#ffffff10' : '#e5e7eb',
                          color: isDarkMode ? '#4b5563' : '#9ca3af',
                        },
                      }}
                    >
                      {verifyLoading ? (
                        <CircularProgress size={18} sx={{ color: 'inherit' }} />
                      ) : (
                        t('iClickedTheLink') || "I've clicked the link"
                      )}
                    </IconButton>
                  </Box>
                ) : (
                  // Phone verification - show code input
                  <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder={codePlaceholder}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: isDarkMode ? '#ffffff08' : '#f9fafb',
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: mainColor,
                            },
                          },
                          '& input': {
                            fontSize: '0.875rem',
                            fontFamily: 'monospace',
                            letterSpacing: 4,
                            textAlign: 'center',
                            color: isDarkMode ? '#fff' : '#111827',
                          },
                        }}
                      />
                      <IconButton
                        onClick={onVerifyCode}
                        disabled={!code || verifyLoading}
                        sx={{
                          width: 44,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: code ? successColor : (isDarkMode ? '#ffffff10' : '#e5e7eb'),
                          color: code ? '#fff' : (isDarkMode ? '#6b7280' : '#9ca3af'),
                          '&:hover': {
                            bgcolor: code ? '#16a34a' : (isDarkMode ? '#ffffff15' : '#d1d5db'),
                          },
                          '&:disabled': {
                            bgcolor: isDarkMode ? '#ffffff10' : '#e5e7eb',
                            color: isDarkMode ? '#4b5563' : '#9ca3af',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {verifyLoading ? (
                          <CircularProgress size={18} sx={{ color: 'inherit' }} />
                        ) : (
                          <ArrowForward sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        color: isDarkMode ? '#6b7280' : '#9ca3af',
                        fontSize: '0.7rem',
                      }}
                    >
                      {t('codeExpiresSoon')}
                    </Typography>
                  </>
                )}
              </Collapse>
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  </Box>
  );
});

const VerificationModal = ({ user, initialType = 'email', onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const businessData = useSelector((state) => state.core.businessData);
  const { mainColor, successColor } = getSemanticColors(businessData, theme);

  const [email, setEmail] = useState(
    user?.activeBusiness?.publicDisplay?.email || user?.email || ''
  );
  const [phone, setPhone] = useState(
    user?.activeBusiness?.publicDisplay?.phone || user?.phone || ''
  );
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');

  const [emailSent, setEmailSent] = useState(false);
  const [phoneSent, setPhoneSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [emailVerifyLoading, setEmailVerifyLoading] = useState(false);
  const [phoneVerifyLoading, setPhoneVerifyLoading] = useState(false);

  const isEmailConfirmed = user?.isEmailConfirmed === true;
  const isPhoneConfirmed = user?.isPhoneConfirmed === true;
  const allVerified = isEmailConfirmed && isPhoneConfirmed;
  
  // Calculate progress
  const progress = (isEmailConfirmed ? 50 : 0) + (isPhoneConfirmed ? 50 : 0);

  useEffect(() => {
    if (allVerified) {
      setTimeout(() => onClose?.(), 5500);
    }
  }, [allVerified, onClose]);

  const handleSendEmailCode = async () => {
    if (!email || emailLoading) return;
    setEmailLoading(true);
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'users/sendVerificationCode',
        body: { type: 'email', value: email },
      });
      if (response?.errorData) {
        throw new Error(response.errorData.message);
      }
      setEmailSent(true);
      toast.success(t('verificationEmailSent') || 'Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(t('errorSendingEmail') || 'Failed to send verification email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    // Check if email has been verified via the link
    setEmailVerifyLoading(true);
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'users/verifyCode',
        body: { type: 'email', code: 'link-verification' },
      });
      if (response?.success) {
        dispatch(setUser({ ...user, isEmailConfirmed: true }));
        toast.success(t('emailVerified') || 'Email verified successfully!');
      } else if (response?.pending) {
        toast.info(t('emailNotYetVerified') || 'Please click the link in your email first.');
      } else {
        toast.error(t('emailVerificationFailed') || 'Email verification failed');
      }
    } catch (error) {
      toast.error(t('errorVerifyingEmail') || 'Error checking email verification status');
    } finally {
      setEmailVerifyLoading(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!phone || phoneLoading) return;
    setPhoneLoading(true);
    
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'users/sendVerificationCode',
        body: { type: 'phone', value: phone },
      });
      
      if (response?.errorData) {
        throw new Error(response.errorData.message);
      }
      
      setPhoneSent(true);
      toast.success(t('verificationCodeSent') || 'Verification code sent!');
    } catch (error) {
      console.error('Phone verification error:', error);
      toast.error(t('errorSendingCode') || 'Failed to send verification code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneCode || phoneVerifyLoading) return;
    setPhoneVerifyLoading(true);
    
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'users/verifyCode',
        body: { type: 'phone', code: phoneCode },
      });
      
      if (response?.success) {
        dispatch(setUser({ ...user, isPhoneConfirmed: true, phone }));
        toast.success(t('phoneVerified') || 'Phone verified successfully!');
      } else {
        toast.error(response?.error || t('invalidCode') || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Phone verification error:', error);
      toast.error(t('errorVerifyingCode') || 'Error verifying code');
    } finally {
      setPhoneVerifyLoading(false);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 480 },
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: 4,
          bgcolor: isDarkMode ? '#1a1a2e' : '#ffffff',
          boxShadow: isDarkMode
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          outline: 'none',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            '&:hover': {
              bgcolor: isDarkMode ? '#ffffff10' : '#f3f4f6',
            },
          }}
        >
          <Close sx={{ fontSize: 20, color:  '#fff'  }} />
        </IconButton>

        {/* Header */}
        <Box
          sx={{
            p: 4,
            pb: 3,
            textAlign: 'center',
            background: allVerified
              ? `linear-gradient(135deg, ${successColor} 0%, ${successColor}dd 100%)`
              : `linear-gradient(135deg, ${mainColor} 0%, ${mainColor}dd 100%)`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px),
                          radial-gradient(circle at 80% 50%, white 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }}
          />

          {/* Shield Icon */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            {allVerified ? (
              <CheckCircle sx={{ fontSize: 40, color: '#fff' }} />
            ) : (
              <Shield sx={{ fontSize: 40, color: '#fff' }} />
            )}
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#fff',
              mb: 1,
              fontSize: '1.4rem',
            }}
          >
            {allVerified ? t('accountSecured') : t('secureYourAccount')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              maxWidth: 320,
              mx: 'auto',
              fontSize: '0.85rem',
              lineHeight: 1.5,
            }}
          >
            {allVerified
              ? t('allVerificationsComplete')
              : t('verificationSecurityMessage')}
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ px: 4, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? '#9ca3af' : '#6b7280',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.65rem',
              }}
            >
              {t('verificationProgress')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: progress === 100 ? successColor : (isDarkMode ? '#fff' : '#111827'),
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            >
              {progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: isDarkMode ? '#ffffff10' : '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: progress === 100 ? successColor : mainColor,
                transition: 'transform 0.5s ease',
              },
            }}
          />
        </Box>

        {/* Verification Cards */}
        <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ order: initialType === 'phone' ? 2 : 1 }}>
          <VerificationCard
            icon={Email}
            title={t('emailVerification')}
            subtitle={t('emailVerificationSubtitle')}
            isVerified={isEmailConfirmed}
            value={email}
            setValue={setEmail}
            code={emailCode}
            setCode={setEmailCode}
            codeSent={emailSent}
            onSendCode={handleSendEmailCode}
            onVerifyCode={handleVerifyEmail}
            sendLoading={emailLoading}
            verifyLoading={emailVerifyLoading}
            placeholder={t('enterEmail')}
            codePlaceholder="000000"
            isEmailType={true}
            isDarkMode={isDarkMode}
            mainColor={mainColor}
            successColor={successColor}
            t={t}
          />
          </Box>

          <Box sx={{ order: initialType === 'phone' ? 1 : 2 }}>
          <VerificationCard
            icon={Phone}
            title={t('phoneVerification')}
            subtitle={t('phoneVerificationSubtitle')}
            isVerified={isPhoneConfirmed}
            value={phone}
            setValue={setPhone}
            code={phoneCode}
            setCode={setPhoneCode}
            codeSent={phoneSent}
            onSendCode={handleSendPhoneCode}
            onVerifyCode={handleVerifyPhone}
            sendLoading={phoneLoading}
            verifyLoading={phoneVerifyLoading}
            placeholder={t('enterPhone')}
            codePlaceholder="000000"
            isPhoneType={true}
            isDarkMode={isDarkMode}
            mainColor={mainColor}
            successColor={successColor}
            t={t}
          />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${isDarkMode ? '#ffffff10' : '#f3f4f6'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <LockOutlined sx={{ fontSize: 14, color: isDarkMode ? '#6b7280' : '#9ca3af' }} />
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? '#6b7280' : '#9ca3af',
              fontSize: '0.7rem',
            }}
          >
            {t('securityFooterMessage')}
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};

export default VerificationModal;

